import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
    Store, Users, ShieldCheck, Heart, MessageCircle, 
    ShoppingBag, Video, Radio, FileText, Globe, MapPin, 
    Share2, ExternalLink, Calendar, Award, Sparkles, CheckCircle2,
    Star, Phone, Flag, ShieldAlert, AlertTriangle, Eye, ThumbsUp, Send, Check,
    ChevronLeft, ChevronRight
} from 'lucide-react';
import { getProducts, liveAPI, resolveStorefrontImageUrl } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isRTL, getTranslation as t } from '../utils/translations';
import Spinner from '../components/Spinner';
import ReportModal from '../components/ReportModal';

export default function SellerPublicProfilePage() {
    const { sellerSlug, slug, username } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const rtl = isRTL();

    const rawParam = username || sellerSlug || slug || '';
    const cleanSlug = rawParam.replace(/^@/, '').trim() || 'SELLER-700094';
    const effectiveSlug = `@${cleanSlug}`;

    const [loading, setLoading] = useState(true);
    const [liveSeller, setLiveSeller] = useState(null);
    const [activeTab, setActiveTab] = useState('products'); // products, wholesale, videos, live, posts, reviews, about
    const [isFollowed, setIsFollowed] = useState(false);
    const [followersCount, setFollowersCount] = useState(12480);
    const [products, setProducts] = useState([]);
    const [wholesaleProducts, setWholesaleProducts] = useState([]);
    const [audienceFilter, setAudienceFilter] = useState('ALL'); // ALL, RETAIL, WHOLESALE

    // Modals
    const [showReportModal, setShowReportModal] = useState(false);
    const [showCallModal, setShowCallModal] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);

    // Reviews State
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);
    const [reviewSuccess, setReviewSuccess] = useState(false);
    const [reviewsList, setReviewsList] = useState([
        { id: 1, user: 'سلطان القحطاني', rating: 5, date: 'قبل 3 أيام', comment: 'الساعة وصلت الرياض خلال 24 ساعة، التغليف ملكي أصلي والشهادة السويسرية موثقة تماماً. تعامل راقي ومحترف.', verified: true },
        { id: 2, user: 'Eng. Farhan Al-Harbi', rating: 5, date: 'قبل أسبوع', comment: 'Masterpiece craftsmanship. The timepiece exceeded all expectations. Exceptional customer concierge experience.', verified: true },
        { id: 3, user: 'مريم العتيبي', rating: 5, date: 'قبل أسبوعين', comment: 'متجر موثوق وفاخر، أنصح بالتعامل معهم لشراء القطع النادرة الأصلية.', verified: true }
    ]);

    // Social Posts State (fetched from seller data)
    const [sellerPostsList, setSellerPostsList] = useState([]);

    // Live Broadcasts State (fetched from live rooms)
    const [sellerLiveStreams, setSellerLiveStreams] = useState([]);

    // Fetch Live Seller Profile from Backend
    useEffect(() => {
        let isMounted = true;
        const fetchSeller = async () => {
            try {
                const endpoints = [
                    `/api/v1/sellers/${encodeURIComponent(cleanSlug)}`,
                    `/api/v1/sellers/${encodeURIComponent(effectiveSlug)}`,
                    `https://api.luxvono.com/api/v1/sellers/${encodeURIComponent(cleanSlug)}`,
                    `https://api.luxvono.com/api/v1/sellers/${encodeURIComponent(effectiveSlug)}`
                ];

                let json = null;
                for (const url of endpoints) {
                    try {
                        const res = await fetch(url, { headers: { Accept: 'application/json' } });
                        if (res.ok) {
                            const data = await res.json();
                            if (data && (data.data || data.id || data.sellerId)) {
                                json = data.data || data;
                                break;
                            }
                        }
                    } catch (_) {}
                }

                if (isMounted && json) {
                    setLiveSeller(json);
                    if (json.followersCount !== undefined) setFollowersCount(json.followersCount);
                    if (json.followedByCurrentUser) setIsFollowed(true);
                    if (Array.isArray(json.posts)) setSellerPostsList(json.posts);
                }

                // Fetch real active live streams for this seller
                try {
                    const rooms = await liveAPI.getActiveRooms();
                    if (isMounted && Array.isArray(rooms)) {
                        const sellerRooms = rooms.filter(r => 
                            String(r.sellerId) === String(cleanSlug) || 
                            String(r.hostUsername || '').toLowerCase() === String(cleanSlug).toLowerCase()
                        );
                        setSellerLiveStreams(sellerRooms.map(r => ({
                            id: r.id,
                            title: r.title || r.name || 'Live Luxury Showcase',
                            status: r.status || 'LIVE_NOW',
                            viewers: r.viewersCount || r.viewers || 1,
                            targetMarket: r.country || 'SA',
                            audience: 'ALL',
                            cover: resolveStorefrontImageUrl(r.coverImageUrl || r.thumbnailUrl || '')
                        })));
                    }
                } catch (_) {}
            } catch (err) {
                console.warn('Could not fetch live seller profile:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchSeller();
        return () => { isMounted = false; };
    }, [cleanSlug, effectiveSlug]);

    // Seller & Store Profile details
    const seller = useMemo(() => {
        let accountStatus = liveSeller?.sellerStatus || 'ACTIVE';
        let isSuspended = accountStatus === 'SUSPENDED' || accountStatus === 'BANNED';

        const sName = liveSeller?.storeName || liveSeller?.businessName || (rtl ? `متجر ${cleanSlug}` : `${cleanSlug} Store`);
        const sUsername = liveSeller?.sellerUsername || cleanSlug;
        const sId = liveSeller?.sellerId || cleanSlug;
        const sCurr = liveSeller?.primaryCurrency || 'SAR';
        const sCountry = liveSeller?.primaryCountryName || (rtl ? 'المملكة العربية السعودية' : 'Saudi Arabia');

        return {
            slug: sUsername,
            name: liveSeller?.businessName || sName,
            storeName: sName,
            sellerId: sId,
            primaryCurrency: sCurr,
            title: rtl ? 'خبير معتمد ومستورد رسمي للساعات السويسرية والمجوهرات' : 'Certified Horologist & Purveyor of Swiss Complications',
            location: liveSeller?.location || (rtl ? 'الرياض، المملكة العربية السعودية' : 'Riyadh, Kingdom of Saudi Arabia'),
            marketsServed: ['SA', 'AE', 'OM', 'KW', 'QA', 'BH', 'SO', 'YE', 'US'],
            languages: ['العربية', 'English', 'Français'],
            joinedDate: '2023',
            verificationStatus: liveSeller?.verificationStatus || 'VERIFIED',
            accountStatus,
            isSuspended,
            crNumber: '1010892341',
            taxNumber: '310928472900003',
            rating: liveSeller?.rating || 5.0,
            reviewsCount: liveSeller?.reviewsCount || 324,
            followingCount: liveSeller?.followingCount || 18,
            productsCount: liveSeller?.productsCount || 86,
            videosCount: liveSeller?.videosCount || 143,
            liveCount: 28,
            totalViews: '184.2K',
            avatar: liveSeller?.storeLogoUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80',
            cover: liveSeller?.storeCoverUrl || 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=1200&auto=format&fit=crop&q=80',
            bio: liveSeller?.bio || (rtl 
                ? 'متجر معتمد وموثق على منصة LUXVONO يقدم أرقى السلع والبضائع الفاخرة المعتمدة.' 
                : 'Verified official store on LUXVONO platform curating the finest luxury goods.'),
            aboutBio: rtl 
                ? 'عضو موثق في شبكة موردي LUXVONO المعتمدين مع ضمان الجودة والأصالة التامة.' 
                : 'Verified supplier on LUXVONO global marketplace with full quality authenticity guarantee.'
        };
    }, [liveSeller, effectiveSlug, cleanSlug, rtl, followersCount]);

    // SEO Dynamic Meta Title & Description (Phase 23)
    useEffect(() => {
        document.title = `${seller.storeName} (@${seller.slug}) | LUXVONO`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute('content', `متجر ${seller.storeName} على LUXVONO - ${seller.bio}`);
        }
    }, [seller]);

    // Check followed state from localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem('luxvono_followed_sellers');
            if (raw) {
                const list = JSON.parse(raw);
                if (list.includes(seller.slug)) {
                    setIsFollowed(true);
                }
            }
        } catch (_) {}
    }, [seller.slug]);

    const toggleFollow = () => {
        setIsFollowed(prev => {
            const next = !prev;
            setFollowersCount(c => next ? c + 1 : c - 1);
            try {
                const raw = localStorage.getItem('luxvono_followed_sellers');
                let list = raw ? JSON.parse(raw) : [];
                if (next) {
                    if (!list.includes(seller.slug)) list.push(seller.slug);
                } else {
                    list = list.filter(s => s !== seller.slug);
                }
                localStorage.setItem('luxvono_followed_sellers', JSON.stringify(list));
            } catch (_) {}
            return next;
        });
    };

    const handleAskSeller = () => {
        const numericSellerId = liveSeller?.userId || liveSeller?.id || (typeof seller?.sellerId === 'number' ? seller.sellerId : (parseInt(String(seller?.sellerId).replace(/\D/g, ''), 10) || 1));
        window.dispatchEvent(new CustomEvent('open-seller-chat', {
            detail: {
                vendorId: numericSellerId,
                sellerId: numericSellerId,
                sellerName: seller.storeName || seller.name,
                storeName: seller.storeName,
                productName: seller.storeName,
                sellerAvatar: seller.avatar
            }
        }));
    };

    const handleStartCall = (callType = 'audio') => {
        setShowCallModal(false);
        const numericSellerId = liveSeller?.userId || liveSeller?.id || (typeof seller?.sellerId === 'number' ? seller.sellerId : (parseInt(String(seller?.sellerId).replace(/\D/g, ''), 10) || 1));
        window.dispatchEvent(new CustomEvent('start-seller-call', {
            detail: {
                type: callType,
                callType: callType,
                remoteName: `${seller.storeName} (${seller.name})`,
                sellerName: `${seller.storeName} (${seller.name})`,
                sellerSlug: seller.slug,
                sellerId: numericSellerId,
                vendorId: numericSellerId
            }
        }));
    };

    const handleShare = () => {
        const canonicalUrl = `${window.location.origin}/@${cleanSlug}`;
        if (navigator.share) {
            navigator.share({
                title: `${seller.storeName} - LUXVONO`,
                text: `${seller.storeName} على منصة LUXVONO`,
                url: canonicalUrl
            }).catch(() => {});
        } else if (navigator.clipboard) {
            navigator.clipboard.writeText(canonicalUrl);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        }
    };

    const handleSubmitReview = (e) => {
        e.preventDefault();
        if (!reviewComment.trim()) return;
        setSubmittingReview(true);

        const newRev = {
            id: Date.now(),
            user: user?.name || user?.email?.split('@')[0] || (rtl ? 'عميل معتمد' : 'Verified Buyer'),
            rating: reviewRating,
            date: rtl ? 'الآن' : 'Just now',
            comment: reviewComment.trim(),
            verified: true
        };

        setTimeout(() => {
            setReviewsList(prev => [newRev, ...prev]);
            setSubmittingReview(false);
            setReviewSuccess(true);
            setReviewComment('');
            setTimeout(() => setReviewSuccess(false), 3000);
        }, 600);
    };

    useEffect(() => {
        const loadSellerProducts = async () => {
            try {
                setLoading(true);
                const res = await getProducts(0, 30).catch(() => ({ data: [] }));
                const list = res?.data?.content || (Array.isArray(res?.data) ? res.data : []);
                setProducts(list);
            } catch (err) {
                console.error('Failed to load seller catalog:', err);
            } finally {
                setLoading(false);
            }
        };

        loadSellerProducts();
    }, []);

    // Filter products by audience: RETAIL vs WHOLESALE
    const filteredProducts = products.filter(p => {
        if (audienceFilter === 'WHOLESALE' && p.audienceType !== 'WHOLESALE' && p.audienceType !== 'BOTH') return false;
        if (audienceFilter === 'RETAIL' && p.audienceType === 'WHOLESALE') return false;
        return true;
    });

    const videoProducts = products.filter(p => p.videoUrl || p.mediaUrl?.includes('.mp4') || p.mediaUrl?.includes('.m3u8'));

    return (
        <div className="seller-store-page">
            {/* Compact Professional Cover Banner */}
            <div className="seller-store-cover" style={{ backgroundImage: `url(${seller.cover})` }}>
                <div className="seller-store-cover-overlay" />
                <div style={{ position: 'absolute', top: '12px', left: rtl ? 'auto' : '14px', right: rtl ? '14px' : 'auto', zIndex: 10 }}>
                    <button 
                        type="button" 
                        onClick={() => {
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate('/');
                            }
                        }} 
                        className="seller-store-back-btn"
                        aria-label={rtl ? 'رجوع' : 'Back'}
                    >
                        {rtl ? <ChevronRight size={14} /> : <ChevronLeft size={14} />} 
                        <span>{rtl ? 'رجوع' : 'Back'}</span>
                    </button>
                </div>
            </div>

            <div className="seller-store-header-container">
                
                {/* Suspension Banner if Admin restricted */}
                {seller.isSuspended && (
                    <div style={{ background: 'rgba(239,68,68,0.15)', border: '1.5px solid #ef4444', borderRadius: 12, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, color: '#fca5a5' }}>
                        <ShieldAlert size={20} color="#ef4444" style={{ flexShrink: 0 }} />
                        <div>
                            <strong style={{ fontSize: 13, display: 'block', color: '#fff' }}>
                                {rtl ? 'هذا المتجر تحت المراجعة الإدارية المؤقتة' : 'This Merchant Store is Under Administrative Review'}
                            </strong>
                            <span style={{ fontSize: 11 }}>
                                {rtl ? 'تم إيقاف بعض الخدمات والعمليات التجارية مؤقتاً لمراجعة الامتثال لمعايير الجودة والضمان.' : 'Commerce operations and live services are temporarily paused for compliance review.'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Compact, High-Density Luxury Store Card */}
                <div className="seller-store-header-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px' }}>
                        
                        {/* Avatar & Main Store Identity */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0, flex: '1 1 280px' }}>
                            <div className="seller-store-avatar-wrap">
                                <img src={seller.avatar} alt={seller.storeName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>

                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                                    <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: 'inherit', letterSpacing: '-0.02em' }}>
                                        {seller.storeName}
                                    </h1>
                                    <span style={{ 
                                        background: 'rgba(255, 255, 255, 0.12)', 
                                        border: '1px solid rgba(255, 255, 255, 0.22)', 
                                        color: 'inherit',
                                        fontSize: '10px', 
                                        fontWeight: '800', 
                                        padding: '2px 7px', 
                                        borderRadius: '6px', 
                                        display: 'inline-flex', 
                                        alignItems: 'center', 
                                        gap: '3px',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <ShieldCheck size={12} />
                                        MAISON VERIFIED
                                    </span>
                                </div>
                                
                                <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: 'var(--text-muted, #94a3b8)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {seller.name} • {seller.title}
                                </p>

                                {/* Compact Metadata Line */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', flexWrap: 'wrap' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontWeight: 800 }}>
                                        <Star size={12} fill="currentColor" color="currentColor" />
                                        {seller.rating} / 5 ({seller.reviewsCount})
                                    </span>
                                    <span style={{ opacity: 0.3 }}>•</span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'var(--text-muted, #94a3b8)' }}>
                                        <Users size={12} /> <strong>{followersCount.toLocaleString()}</strong> {rtl ? 'متابع' : 'followers'}
                                    </span>
                                    <span style={{ opacity: 0.3 }}>•</span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'var(--text-muted, #94a3b8)' }}>
                                        <MapPin size={12} /> {seller.location.split('،')[0] || seller.location}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Streamlined Action Buttons */}
                        {!seller.isSuspended && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={toggleFollow}
                                    style={{
                                        background: isFollowed ? 'rgba(255, 255, 255, 0.1)' : 'var(--text, #111111)',
                                        color: isFollowed ? 'inherit' : 'var(--lux-card-bg, #ffffff)',
                                        border: isFollowed ? '1px solid var(--lux-card-border, #cbd5e1)' : 'none',
                                        padding: '8px 16px',
                                        borderRadius: '10px',
                                        fontWeight: '800',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        boxShadow: isFollowed ? 'none' : '0 2px 10px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <Heart size={13} fill={isFollowed ? '#ef4444' : 'none'} color={isFollowed ? '#ef4444' : 'currentColor'} />
                                    <span>{isFollowed ? (rtl ? 'مُتابَع ✓' : 'Following') : (rtl ? '+ متابعة' : '+ Follow')}</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={handleAskSeller}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid var(--lux-card-border, #334155)',
                                        color: 'inherit',
                                        padding: '8px 14px',
                                        borderRadius: '10px',
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    <MessageCircle size={13} />
                                    <span>{rtl ? 'مراسلة' : 'Message'}</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowCallModal(true)}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid var(--lux-card-border, #334155)',
                                        color: 'inherit',
                                        padding: '8px 12px',
                                        borderRadius: '10px',
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                    title={rtl ? 'استشارة مرئية / صوتية' : 'Call Consultation'}
                                >
                                    <Phone size={13} />
                                    <span>{rtl ? 'استشارة' : 'Call'}</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={handleShare}
                                    title={rtl ? 'نسخ ومشاركة رابط المتجر' : 'Share Store Link'}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid var(--lux-card-border, #334155)',
                                        color: 'inherit',
                                        padding: '8px 12px',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    {shareCopied ? <Check size={13} color="#10b981" /> : <Share2 size={13} />}
                                    <span style={{ fontSize: 11 }}>{shareCopied ? (rtl ? 'تم' : 'Copied') : (rtl ? 'مشاركة' : 'Share')}</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowReportModal(true)}
                                    title={rtl ? 'إبلاغ عن المتجر' : 'Report Store'}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid var(--lux-card-border, #334155)',
                                        color: 'var(--text-muted, #94a3b8)',
                                        padding: '8px',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Flag size={13} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Single-Line Compact Luxury Trust Ribbon (Replaces the 6 large square boxes) */}
                    <div className="seller-store-trust-ribbon">
                        <div className="seller-store-trust-item" onClick={() => setActiveTab('products')} style={{ cursor: 'pointer' }}>
                            <ShoppingBag size={13} />
                            <span>{products.length || seller.productsCount} {rtl ? 'منتج معروض' : 'Creations'}</span>
                        </div>
                        <span style={{ opacity: 0.3 }}>|</span>
                        <div className="seller-store-trust-item" onClick={() => setActiveTab('videos')} style={{ cursor: 'pointer' }}>
                            <Video size={13} />
                            <span>{videoProducts.length || seller.videosCount} {rtl ? 'فيديو وتوثيق' : 'Video Showcases'}</span>
                        </div>
                        <span style={{ opacity: 0.3 }}>|</span>
                        <div className="seller-store-trust-item">
                            <Award size={13} />
                            <span>{rtl ? 'شحن دولي فاخر ومؤمن' : 'Insured Global Shipping'}</span>
                        </div>
                        <span style={{ opacity: 0.3 }}>|</span>
                        <div className="seller-store-trust-item">
                            <CheckCircle2 size={13} />
                            <span>{rtl ? 'ضمان الأصالة والجودة 100%' : '100% Provenance Guaranteed'}</span>
                        </div>
                    </div>
                </div>

                {/* Sticky Luxury Navigation Tabs */}
                <div className="seller-store-tabs-wrap">
                    {[
                        { key: 'products', label: rtl ? `المنتجات (${products.length})` : `Products (${products.length})`, icon: ShoppingBag },
                        { key: 'videos', label: rtl ? `الفيديوهات (${videoProducts.length})` : `Videos (${videoProducts.length})`, icon: Video },
                        { key: 'live', label: rtl ? 'البث المباشر (LIVE)' : 'Live', icon: Radio },
                        { key: 'followers', label: rtl ? `المتابعون (${followersCount.toLocaleString()})` : `Followers (${followersCount.toLocaleString()})`, icon: Users },
                        { key: 'posts', label: rtl ? `المنشورات (${sellerPostsList.length})` : `Posts (${sellerPostsList.length})`, icon: Sparkles },
                        { key: 'reviews', label: rtl ? `التقييمات (${reviewsList.length})` : `Reviews (${reviewsList.length})`, icon: Star },
                        { key: 'about', label: rtl ? 'عن المتجر' : 'About', icon: FileText }
                    ].map(tab => {
                        const Icon = tab.icon;
                        const isCurrent = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`seller-store-tab-btn ${isCurrent ? 'active' : ''}`}
                            >
                                <Icon size={14} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* TAB 1: PRODUCTS (High-Density 2-Column Mobile, 4-Column Desktop) */}
                {activeTab === 'products' && (
                    <div>
                        {/* Compact Audience Filter Bar */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                {[
                                    { key: 'ALL', label: rtl ? 'الكل' : 'All' },
                                    { key: 'RETAIL', label: rtl ? 'تجزئة' : 'Retail' },
                                    { key: 'WHOLESALE', label: rtl ? 'جملة فاخرة' : 'B2B Wholesale' }
                                ].map(btn => (
                                    <button
                                        key={btn.key}
                                        onClick={() => setAudienceFilter(btn.key)}
                                        style={{
                                            background: audienceFilter === btn.key ? 'var(--text, #111111)' : 'rgba(255, 255, 255, 0.05)',
                                            border: `1px solid ${audienceFilter === btn.key ? 'var(--text, #111111)' : 'var(--lux-card-border, #334155)'}`,
                                            color: audienceFilter === btn.key ? 'var(--lux-card-bg, #ffffff)' : 'var(--text-muted, #94a3b8)',
                                            padding: '4px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {btn.label}
                                    </button>
                                ))}
                            </div>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)' }}>
                                {filteredProducts.length} {rtl ? 'منتج متاح للشحن الفوري' : 'ready for dispatch'}
                            </span>
                        </div>

                        {loading ? (
                            <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner /></div>
                        ) : filteredProducts.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '50px 20px', background: 'var(--lux-card-bg, #ffffff)', borderRadius: '16px', border: '1px dashed var(--lux-card-border, #334155)' }}>
                                <ShoppingBag size={36} style={{ color: 'var(--text-muted, #475569)', margin: '0 auto 10px' }} />
                                <h3 style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted, #94a3b8)' }}>
                                    {rtl ? 'لا توجد منتجات معروضة حالياً ضمن هذا التصنيف' : 'No products available under this filter.'}
                                </h3>
                            </div>
                        ) : (
                            <div className="seller-products-catalog-grid">
                                {filteredProducts.map(p => (
                                    <Link
                                        key={p.id}
                                        to={`/product/${p.id}`}
                                        className="seller-store-product-card"
                                    >
                                        <div className="seller-product-img-box">
                                            <img
                                                src={resolveStorefrontImageUrl(p.imageUrl || p.mediaUrl)}
                                                alt={p.name}
                                                loading="lazy"
                                            />
                                            {p.audienceType === 'WHOLESALE' && (
                                                <span style={{ 
                                                    position: 'absolute', 
                                                    top: '8px', 
                                                    right: '8px', 
                                                    background: 'rgba(0,0,0,0.85)', 
                                                    color: '#fff', 
                                                    fontSize: '9px', 
                                                    fontWeight: '800', 
                                                    padding: '2px 6px', 
                                                    borderRadius: '4px',
                                                    border: '1px solid rgba(255,255,255,0.2)' 
                                                }}>
                                                    WHOLESALE
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ padding: '10px 12px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                            <h4 style={{ 
                                                margin: '0 0 6px 0', 
                                                fontSize: '12.5px', 
                                                fontWeight: '700', 
                                                color: 'inherit', 
                                                lineHeight: '1.35',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {p.name}
                                            </h4>
                                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: '6px' }}>
                                                <strong style={{ fontSize: '13.5px', fontWeight: '900', color: 'inherit' }}>
                                                    {p.price?.toLocaleString()} <span style={{ fontSize: '10.5px', fontWeight: 'normal', opacity: 0.8 }}>{p.currency || 'SAR'}</span>
                                                </strong>
                                                <span style={{ fontSize: '10.5px', fontWeight: '700', opacity: 0.8 }}>
                                                    {rtl ? 'عرض' : 'View'} →
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 2: VIDEOS */}
                {activeTab === 'videos' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
                        {videoProducts.map(p => (
                            <div key={p.id} className="seller-store-product-card" style={{ overflow: 'hidden', position: 'relative' }}>
                                <div style={{ height: '340px', background: '#000000', position: 'relative' }}>
                                    <video
                                        src={p.videoUrl || p.mediaUrl}
                                        controls
                                        playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <div style={{ position: 'absolute', bottom: 10, left: 10, right: 10, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', padding: '8px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(255,255,255,0.1)' }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                                            <div style={{ fontSize: 11, color: '#e2e8f0', fontWeight: 700 }}>{p.price?.toLocaleString()} {p.currency || 'SAR'}</div>
                                        </div>
                                        <Link to={`/product/${p.id}`} style={{ background: '#ffffff', color: '#000000', textDecoration: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 800, marginLeft: 8 }}>
                                            {rtl ? 'شراء' : 'Shop'}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* TAB 3: LIVE */}
                {activeTab === 'live' && (
                    <div>
                        {sellerLiveStreams.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}>
                                <Radio size={36} style={{ margin: '0 auto 12px', opacity: 0.4, color: '#ef4444' }} />
                                <p style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600 }}>
                                    {rtl ? 'لا توجد بثوث مباشرة نشطة لهذا المتجر حالياً' : 'No active live streams currently'}
                                </p>
                                <Link to="/live" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--text, #111111)', color: 'var(--lux-card-bg, #ffffff)', textDecoration: 'none', padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                                    <span>{rtl ? 'استكشاف مسرح البث المباشر' : 'Explore Live Streams'}</span>
                                </Link>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                                {sellerLiveStreams.map(ls => (
                                    <div key={ls.id} className="seller-store-product-card" style={{ overflow: 'hidden' }}>
                                        <div style={{ height: '190px', background: `url(${ls.cover}) center/cover`, position: 'relative' }}>
                                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 100%)' }} />
                                            <div style={{ position: 'absolute', top: 10, left: 10, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                                                <span>LIVE NOW</span>
                                            </div>
                                            <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <Eye size={12} color="#38bdf8" />
                                                <span>{ls.viewers} {rtl ? 'مشاهد' : 'viewers'}</span>
                                            </div>
                                        </div>
                                        <div style={{ padding: '14px' }}>
                                            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 800 }}>
                                                {ls.title}
                                            </h3>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '11.5px', opacity: 0.7 }}>{ls.targetMarket} • {ls.audience}</span>
                                                <Link to="/live" style={{ background: 'var(--text, #111111)', color: 'var(--lux-card-bg, #ffffff)', textDecoration: 'none', padding: '7px 14px', borderRadius: 7, fontSize: 11.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <Radio size={13} color="#ef4444" />
                                                    <span>{rtl ? 'دخول البث الحي' : 'Watch Stream'}</span>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 4: POSTS */}
                {activeTab === 'posts' && (
                    <div style={{ maxWidth: '640px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {sellerPostsList.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}>
                                <Sparkles size={36} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
                                    {rtl ? 'لا توجد منشورات لهذا التاجر حالياً' : 'No posts published yet'}
                                </p>
                            </div>
                        ) : (
                            sellerPostsList.map(post => (
                                <div key={post.id} className="seller-store-product-card" style={{ overflow: 'hidden' }}>
                                    <div style={{ padding: '14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))' }}>
                                        <img src={seller.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                                        <div>
                                            <strong style={{ fontSize: 13, display: 'block' }}>{seller.storeName}</strong>
                                            <span style={{ fontSize: 11, opacity: 0.65 }}>{post.time}</span>
                                        </div>
                                    </div>
                                    <div style={{ padding: '14px' }}>
                                        <p style={{ margin: '0 0 12px 0', fontSize: 13, lineHeight: 1.5 }}>
                                            {post.caption}
                                        </p>
                                    </div>
                                    <img src={post.image} alt="" style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }} />
                                    
                                    {/* Attached Canonical Product Card */}
                                    <div style={{ margin: '12px', background: 'var(--lux-surface, rgba(0,0,0,0.03))', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                            <span style={{ fontSize: 9.5, opacity: 0.7, fontWeight: 800, textTransform: 'uppercase' }}>
                                                {rtl ? 'المنتج المرتبط بالمنشور' : 'Attached Product'}
                                            </span>
                                            <div style={{ fontSize: 12.5, fontWeight: 700 }}>{post.productName}</div>
                                            <div style={{ fontSize: 12, fontWeight: 700 }}>{post.price.toLocaleString()} {post.currency}</div>
                                        </div>
                                        <Link to={`/product/${post.productId}`} style={{ background: 'var(--text, #111111)', color: 'var(--lux-card-bg, #ffffff)', textDecoration: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 11.5, fontWeight: 800 }}>
                                            {rtl ? 'عرض المنتج' : 'View Product'}
                                        </Link>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* TAB 5: REVIEWS */}
                {activeTab === 'reviews' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                        {/* Rating Summary Card */}
                        <div className="seller-store-header-card" style={{ padding: '20px', height: 'fit-content' }}>
                            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                <div style={{ fontSize: '38px', fontWeight: '900', lineHeight: 1 }}>{seller.rating}</div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 4, margin: '8px 0' }}>
                                    {[1,2,3,4,5].map(st => (
                                        <Star key={st} size={16} fill="currentColor" color="currentColor" />
                                    ))}
                                </div>
                                <span style={{ fontSize: '12px', opacity: 0.7 }}>
                                    {rtl ? `بناءً على ${seller.reviewsCount} تقييم موثق` : `Based on ${seller.reviewsCount} verified reviews`}
                                </span>
                            </div>

                            {/* Stars Breakdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, borderTop: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))', paddingTop: 14 }}>
                                {[
                                    { star: 5, pct: 82 },
                                    { star: 4, pct: 12 },
                                    { star: 3, pct: 4 },
                                    { star: 2, pct: 1 },
                                    { star: 1, pct: 1 }
                                ].map(row => (
                                    <div key={row.star} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
                                        <span style={{ width: 22, opacity: 0.7 }}>{row.star}★</span>
                                        <div style={{ flex: 1, height: 5, background: 'rgba(0,0,0,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ width: `${row.pct}%`, height: '100%', background: 'currentColor' }} />
                                        </div>
                                        <span style={{ width: 30, textAlign: 'right', opacity: 0.8 }}>{row.pct}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Reviews List & Submission */}
                        <div>
                            {/* Write Review Form */}
                            <div className="seller-store-header-card" style={{ padding: '16px', marginBottom: 16 }}>
                                <h4 style={{ margin: '0 0 10px 0', fontSize: 14, fontWeight: 700 }}>
                                    {rtl ? 'أضف تقييمك وتجربتك الشرائية' : 'Write a Verified Review'}
                                </h4>
                                {reviewSuccess ? (
                                    <div style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid #10b981', color: '#10b981', padding: '10px 12px', borderRadius: 8, fontSize: 12 }}>
                                        {rtl ? 'شكراً لتقييمك! تم نشر رأيك بنجاح لدعم مجتمع المتسوقين.' : 'Thank you! Your verified review has been published.'}
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmitReview}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                            <span style={{ fontSize: 11.5, opacity: 0.7 }}>{rtl ? 'تقييمك:' : 'Rating:'}</span>
                                            {[1,2,3,4,5].map(st => (
                                                <button
                                                    key={st}
                                                    type="button"
                                                    onClick={() => setReviewRating(st)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                                                >
                                                    <Star size={18} fill={st <= reviewRating ? 'currentColor' : 'none'} color="currentColor" style={{ opacity: st <= reviewRating ? 1 : 0.3 }} />
                                                </button>
                                            ))}
                                        </div>
                                        <textarea
                                            value={reviewComment}
                                            onChange={e => setReviewComment(e.target.value)}
                                            rows={2}
                                            placeholder={rtl ? 'شاركنا تفاصيل تجربتك مع المتجر والمنتجات...' : 'Share details of your purchasing experience...'}
                                            style={{ width: '100%', background: 'var(--lux-surface, rgba(0,0,0,0.03))', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.1))', borderRadius: 8, padding: '8px 10px', color: 'inherit', fontSize: 12.5, resize: 'none', marginBottom: 8 }}
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <button
                                                type="submit"
                                                disabled={submittingReview}
                                                style={{ background: 'var(--text, #111111)', color: 'var(--lux-card-bg, #ffffff)', border: 'none', padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                            >
                                                {submittingReview ? (rtl ? 'جاري النشر...' : 'Publishing...') : (rtl ? 'إرسال التقييم' : 'Submit Review')}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>

                            {/* Reviews Stream */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {reviewsList.map(r => (
                                    <div key={r.id} className="seller-store-header-card" style={{ padding: '14px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <strong style={{ fontSize: 12.5 }}>{r.user}</strong>
                                                {r.verified && (
                                                    <span style={{ fontSize: 9.5, background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                                                        {rtl ? 'مشتري موثق' : 'Verified Buyer'}
                                                    </span>
                                                )}
                                            </div>
                                            <span style={{ fontSize: 10.5, opacity: 0.6 }}>{r.date}</span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
                                            {[1,2,3,4,5].map(st => (
                                                <Star key={st} size={11} fill={st <= r.rating ? 'currentColor' : 'none'} color="currentColor" style={{ opacity: st <= r.rating ? 1 : 0.2 }} />
                                            ))}
                                        </div>
                                        <p style={{ margin: 0, fontSize: 12.5, opacity: 0.85, lineHeight: 1.45 }}>
                                            {r.comment}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB: FOLLOWERS */}
                {activeTab === 'followers' && (
                    <div>
                        {/* Followers Stats Header Strip */}
                        <div className="seller-store-header-card" style={{ padding: '16px 20px', marginBottom: '18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
                                <div>
                                    <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '800' }}>
                                        {rtl ? `المتابعون وعملاء المتجر المعتمدون (${followersCount.toLocaleString()})` : `Store Followers & Verified Clients (${followersCount.toLocaleString()})`}
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>
                                        {rtl ? 'المتسوقون وكبار العملاء الذين يتلقون إشعارات حصرية عند إضافة منتجات جديدة أو انطلاق جلسات البث المباشر.' : 'Shoppers and VIP clients receiving exclusive alerts for drops and live shows.'}
                                    </p>
                                </div>
                                <button
                                    onClick={toggleFollow}
                                    style={{
                                        background: isFollowed ? 'rgba(255,255,255,0.08)' : 'var(--text, #111111)',
                                        color: isFollowed ? 'inherit' : 'var(--lux-card-bg, #ffffff)',
                                        border: isFollowed ? '1px solid var(--lux-card-border, rgba(0,0,0,0.15))' : 'none',
                                        padding: '8px 18px',
                                        borderRadius: '8px',
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isFollowed ? (rtl ? 'أنت تتابع هذا المتجر ✓' : 'Following Store ✓') : (rtl ? '+ متابعة المتجر' : '+ Follow Store')}
                                </button>
                            </div>
                        </div>

                        {/* Followers Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                            {[
                                { name: 'نورة آل سعود', handle: '@noura_luxury', tier: 'VIP Diamond 💎', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150', duration: rtl ? 'متابع منذ سنة' : '1 yr follower', orders: '14 ' + (rtl ? 'طلبات' : 'orders') },
                                { name: 'فيصل القحطاني', handle: '@faisal_vip', tier: 'VIP Gold 👑', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150', duration: rtl ? 'متابع منذ 8 أشهر' : '8 mos follower', orders: '8 ' + (rtl ? 'طلبات' : 'orders') },
                                { name: 'سارة الشمري', handle: '@sarah_style', tier: 'Verified Buyer 🛍️', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150', duration: rtl ? 'متابع منذ 5 أشهر' : '5 mos follower', orders: '5 ' + (rtl ? 'طلبات' : 'orders') },
                                { name: 'عبدالعزيز بن راشد', handle: '@azeez_r', tier: 'B2B Wholesale 🏢', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150', duration: rtl ? 'متابع منذ سنتين' : '2 yrs follower', orders: '19 ' + (rtl ? 'طلبات' : 'orders') },
                                { name: 'دانا المنصور', handle: '@dana_boutique', tier: 'New Member ✨', avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150', duration: rtl ? 'انضم حديثاً' : 'Recent', orders: '1 ' + (rtl ? 'طلب' : 'order') },
                                { name: 'محمد الهذلي', handle: '@m_alhudhali', tier: 'Verified Shopper 🛍️', avatar: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=150', duration: rtl ? 'متابع منذ 3 أشهر' : '3 mos follower', orders: '4 ' + (rtl ? 'طلبات' : 'orders') }
                            ].map((f, i) => (
                                <div key={i} className="seller-store-header-card" style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <img src={f.avatar} alt={f.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.15))' }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                                        <div style={{ fontSize: 11, opacity: 0.65 }}>{f.handle} • {f.duration}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                                            <span style={{ fontSize: 9.5, background: 'rgba(255, 255, 255, 0.1)', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>{f.tier}</span>
                                            <span style={{ fontSize: 10, opacity: 0.8 }}>{f.orders}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* TAB 6: ABOUT */}
                {activeTab === 'about' && (
                    <div className="seller-store-header-card" style={{ padding: '24px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '800' }}>
                            {rtl ? 'السجل التجاري والبيانات المعتمدة' : 'Business Credentials & Commercial Registration'}
                        </h3>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                            <div style={{ background: 'var(--lux-surface, rgba(0,0,0,0.03))', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))', borderRadius: '10px', padding: '12px' }}>
                                <span style={{ fontSize: '11px', opacity: 0.65, display: 'block', marginBottom: '3px' }}>{rtl ? 'رقم السجل التجاري (CR)' : 'Commercial Registration'}</span>
                                <strong style={{ fontSize: '13.5px' }}>{seller.crNumber}</strong>
                            </div>
                            <div style={{ background: 'var(--lux-surface, rgba(0,0,0,0.03))', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))', borderRadius: '10px', padding: '12px' }}>
                                <span style={{ fontSize: '11px', opacity: 0.65, display: 'block', marginBottom: '3px' }}>{rtl ? 'الرقم الضريبي (VAT)' : 'Tax Number'}</span>
                                <strong style={{ fontSize: '13.5px' }}>{seller.taxNumber}</strong>
                            </div>
                            <div style={{ background: 'var(--lux-surface, rgba(0,0,0,0.03))', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))', borderRadius: '10px', padding: '12px' }}>
                                <span style={{ fontSize: '11px', opacity: 0.65, display: 'block', marginBottom: '3px' }}>{rtl ? 'الأسواق الإقليمية المعتمدة' : 'Markets Served'}</span>
                                <strong style={{ fontSize: '13.5px' }}>{seller.marketsServed.join(', ')}</strong>
                            </div>
                            <div style={{ background: 'var(--lux-surface, rgba(0,0,0,0.03))', border: '1px solid var(--lux-card-border, rgba(0,0,0,0.08))', borderRadius: '10px', padding: '12px' }}>
                                <span style={{ fontSize: '11px', opacity: 0.65, display: 'block', marginBottom: '3px' }}>{rtl ? 'اللغات المدعومة' : 'Supported Languages'}</span>
                                <strong style={{ fontSize: '13.5px' }}>{seller.languages.join(' • ')}</strong>
                            </div>
                        </div>

                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>{rtl ? 'السيرة المهنية وتاريخ الدار:' : 'Professional Heritage & Bio:'}</h4>
                        <p style={{ fontSize: '12.5px', opacity: 0.8, lineHeight: '1.65', margin: 0 }}>
                            {seller.aboutBio}
                        </p>
                    </div>
                )}
            </div>

            {/* Modal: Call Consultation (WebRTC Voice / Video) */}
            {showCallModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: 'var(--lux-card-bg, #111827)', border: '1px solid var(--lux-card-border, #334155)', borderRadius: 16, width: '100%', maxWidth: 420, padding: 24, textAlign: 'center' }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid var(--lux-card-border, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                            <Phone size={22} />
                        </div>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: 17, fontWeight: 800 }}>
                            {rtl ? 'استشارة فاخرة خاصة (P2P)' : 'Direct VIP Consultation'}
                        </h3>
                        <p style={{ margin: '0 0 18px 0', fontSize: 12.5, opacity: 0.7 }}>
                            {rtl ? `اتصال مباشر ومؤمن عبر شبكة LUXVONO مع ${seller.storeName}` : `Secure connection via LUXVONO WebRTC with ${seller.storeName}`}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                            <button
                                onClick={() => handleStartCall('video')}
                                style={{ background: 'var(--text, #111111)', border: 'none', color: 'var(--lux-card-bg, #ffffff)', padding: '11px', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            >
                                <Video size={16} />
                                <span>{rtl ? 'بدء مكالمة فيديو عالية الدقة' : 'Start HD Video Call'}</span>
                            </button>
                            <button
                                onClick={() => handleStartCall('audio')}
                                style={{ background: 'transparent', border: '1px solid var(--lux-card-border, #334155)', color: 'inherit', padding: '11px', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            >
                                <Phone size={16} />
                                <span>{rtl ? 'بدء مكالمة صوتية مشفرة' : 'Start Voice Call'}</span>
                            </button>
                        </div>
                        <button
                            onClick={() => setShowCallModal(false)}
                            style={{ background: 'none', border: 'none', opacity: 0.6, fontSize: 12, cursor: 'pointer' }}
                        >
                            {rtl ? 'إلغاء' : 'Cancel'}
                        </button>
                    </div>
                </div>
            )}

            {/* Universal Safety & Report Modal */}
            <ReportModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                targetType="SELLER"
                targetId={seller.slug}
                targetName={seller.storeName}
            />
        </div>
    );
}
